import "./ticket-csv-uploader.styles.scss";
import { useMutation } from "@apollo/client";
import React, { useState, useEffect, useContext } from "react";
import Papa from "papaparse";
import { UserContext } from "../../contexts/user.context.jsx";
import { MessagesContext } from "../../contexts/messages.context.jsx";
import { BATCH_UPLOAD_PENDING_TICKETS, RUN_AUTOMATION } from "../../api/mutations/pending-tickets.mutations";
import TicketReportModal from "./TicketReportModal.jsx";

import Loader from "../loader/loader.component.jsx";

function TicketCSVUploader() {
  const { currentUser } = useContext(UserContext);
  const { addMessage } = useContext(MessagesContext);
  const [data, setData] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadTickets] = useMutation(BATCH_UPLOAD_PENDING_TICKETS);
  const [runAutomation] = useMutation(RUN_AUTOMATION);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [ticketReport, setTicketReport] = useState(null);
  
  const uploadFile = async (data) => {
    setUploading(true);
    try {
      const badRecords = [];
      const validRecords = [];

      for (const record of data) {
        const { asset_serial, make, model } = record;
        if (!asset_serial?.trim() || !make?.trim() || !model?.trim()) {
          badRecords.push(record);
        } else {
          validRecords.push(record);
        }
      }

      // console.log("Processing valid records for upload:", validRecords);
      // console.log("Bad Records:", badRecords);
      if (validRecords.length === 0) {
        addMessage("No valid records to upload.", "error");
        setTicketReport({ totalTickets: 0, createdTickets: [], failedTickets: [], validRecords, badRecords });
        setIsReportOpen(true);
        return;
      }

      const response = await uploadTickets({ variables: { input: data } });
      if (!response || response?.errors) {
        return addMessage(response.errors[0].message, "error");
      }
  
      console.log("Starting automation at:", new Date().toISOString());
      const automationResult = await runAutomation();
      console.log("Automation completed at:", new Date().toISOString());
      const TicketCreationReportData = automationResult?.data?.runTicketAutomation;
      if (!TicketCreationReportData) {
        return addMessage("Automation failed to return report.", "error");
      }
  
      const { totalTickets, createdTickets, failedTickets } = TicketCreationReportData;
  
      const successfulTicketsCount = createdTickets.length || 0;
      const failedTicketsCount = failedTickets.length || 0;
  
      const summaryMessage = `${totalTickets} Tickets processed.\n${successfulTicketsCount} Created successfully.\n${failedTicketsCount} Failed to create.`;
      
      addMessage(`${successfulTicketsCount} Tickets created successfully.`, "success");
  
      console.log(summaryMessage);
      
      // console.log("Created Tickets:", createdTickets);
      // console.log("Failed Tickets:", failedTickets);
      // console.log("Bad Records:", badRecords);
      
      setTicketReport({ totalTickets, createdTickets, failedTickets, validRecords, badRecords });
      setIsReportOpen(true);
  
    } catch (error) {
      return addMessage(error.message, "error");
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (data.length > 0) uploadFile(data);
  }, [data]);

  const processCSV = (data, delim = ",") => {
    if (!!data.lenght) alert("Invalid CSV file: no data found.");
    // Remove commas from within fields
    const removeCommas = data.map((row) => {
      return row.map((col) => {
        if (col.includes(",")) return col?.replace(/,/g, ";");
        return col;
      });
    });
    // Rejoin the rows with the delimiter
    const str = removeCommas.map((row) => row.join(delim)).join("\n");

    const headers = str.slice(0, str.indexOf("\n")).split(delim);
    const rows = str.slice(str.indexOf("\n") + 1).split("\n");
    const indices = [
      headers.indexOf("Serial Number"),
      headers.indexOf("Make"),
      headers.indexOf("Model"),
      headers.indexOf("Asset Type"),
      headers.indexOf("(Optional)Service Tag"),
      headers.indexOf("Problem Type"),
      headers.indexOf("Comment"),
      headers.indexOf("(Optional)Tracking Number"),
    ];

    // Verify that all headers are present
    if (indices.includes(-1)) {
      throw new Error(
        "Invalid CSV file: missing one or more required headers."
      );
    }

    const newData = rows.map((row) => {
      const values = row.split(delim);
      return {
        asset_serial: values[indices[0]],
        make: values[indices[1]],
        model: values[indices[2]],
        asset_type: values[indices[3]],
        service_tag: values[indices[4]],
        problem_type: values[indices[5]],
        comment: values[indices[6]],
        tracking_number: values[indices[7]]?.replace(/ /g, ""), // Remove spaces from tracking number
        customer_id: currentUser.customer_id,
        contact_id: currentUser.id,
      };
    });
    setData(newData);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    Papa.parse(file, {
      skipEmptyLines: true,
      complete: function (results) {
        try {
          // Filter out rows where all columns are empty or whitespace
          const cleanedRaw = results.data.filter(
            (row) => Array.isArray(row) && row.some((col) => col.trim() !== "")
          );
          console.log("cleanedRaw CSV data:", cleanedRaw);
          processCSV(cleanedRaw);
        } catch (e) {
          alert(e.message || e);
        }
      },
    });
    e.target.value = null;
  };

  return (
    <div className="ticket-csv-uploader">

      <TicketReportModal
        isOpen={isReportOpen}
        onClose={() => setIsReportOpen(false)}
        report={ticketReport}
      />

      {uploading && (
        <Loader/>
      )}

      <input
        type="file"
        id="csvFileUpload"
        accept=".csv"
        onChange={handleFileUpload}
        style={{ display: "none" }}
      />
      <label
        htmlFor="csvFileUpload"
        style={{
          cursor: uploading ? "not-allowed" : "pointer",
          backgroundColor: uploading ? "grey" : "",
          opacity: uploading ? 0.5 : 1,
        }}
      >
        {uploading ? "Uploading..." : "Upload Tickets"}
      </label>
    </div>
  );
}

export default TicketCSVUploader;
